import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Navbar from "./Navbar";
import { Camera } from 'react-bootstrap-icons';
import { Tabs, Tab, Container, Row, Col, Form, Button, Card, Image, Table, Spinner, Modal } from 'react-bootstrap';
import API_CONFIG, { buildApiUrl, buildApiUrlWithParams, buildS3Url } from '../config/api';

const StudentDetail = () => {
  const { id } = useParams(); // Get student ID from the URL
  const navigate = useNavigate();
  const [student, setStudent] = useState({
    first_name: "",
    last_name: "",
    nick_name: "",
    date_of_birth: "",
    phone: "",
    email: "",
    current_rank: "",
    current_stripes: "0",
    is_junior: false,
    date_last_promoted: "",
    tuition_amount: "120.00",
    tuition_due_day: 1,
    student_pic_guid: "",
    active: true,
    street_1: "",
    street_2: "",
    city: "",
    state: "",
    zip: "",
    medical_conditions: "",
    allergies: "",
    release_pic_guid: "",
    aau_number: ""
  });
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [groups, setGroups] = useState("");
  const [errors, setErrors] = useState({});
  const [uploading, setUploading] = useState(false);
  const isNewStudent = !id;
  const [guardians, setGuardians] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [guardianToDelete, setGuardianToDelete] = useState(null);
  const [editingGuardian, setEditingGuardian] = useState(null);
  const [showNewGuardianForm, setShowNewGuardianForm] = useState(false);
  const [newGuardian, setNewGuardian] = useState({
    first_name: "",
    last_name: "",
    relationship: "",
    email: "",
    phone: "",
    financial_notification_ok: false
  });

  // Promotion editing state
  const [editingPromotion, setEditingPromotion] = useState(null);
  const [beltRanks, setBeltRanks] = useState([]);
  const [editingPromotionIndex, setEditingPromotionIndex] = useState(null);

  // Validation functions
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone) => {
    const phoneRegex = /^\d{3}-\d{3}-\d{4}$/;
    return phoneRegex.test(phone);
  };

  const validateDate = (date) => {
    const dateRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12]\d|3[01])\/\d{4}$/;
    return dateRegex.test(date);
  };

  const validateTuition = (amount) => {
    if (!amount) return true; // Allow empty values
    const tuitionRegex = /^\d+(?:\.\d{1,2})?$/;
    return tuitionRegex.test(amount);
  };

  const validateState = (state) => {
    if (!state) return true; // Allow empty values
    const stateRegex = /^[A-Z]{2}$/;
    return stateRegex.test(state);
  };

  const validateZip = (zip) => {
    if (!zip) return true; // Allow empty values
    const zipRegex = /^\d{5}$/;
    return zipRegex.test(zip);
  };

  const formatPhoneNumber = (value) => {
    // Remove all non-digits
    const cleaned = value.replace(/\D/g, '');
    // Format as ###-###-####
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return match[1] + '-' + match[2] + '-' + match[3];
    }
    return value;
  };

  // ✅ Convert MM/DD/YYYY or MM-DD-YYYY to YYYY-MM-DD for <input type="date">
  const formatDateForInput = (dateString) => {
    if (!dateString) return "";
    // Handle both MM/DD/YYYY and MM-DD-YYYY formats
    const parts = dateString.split(/[/-]/);
    if (parts.length !== 3) return "";
    const [month, day, year] = parts;
    return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
  };

  // ✅ Convert YYYY-MM-DD back to MM/DD/YYYY for API submission
  const formatDateForAPI = (dateString) => {
    if (!dateString) return "";
    const [year, month, day] = dateString.split("-");
    return `${month}/${day}/${year}`;
  };

  // ✅ Convert YYYY-MM-DD to MM/DD/YYYY for display
  const formatDateForDisplay = (dateString) => {
    if (!dateString) return "";
    const [year, month, day] = dateString.split("-");
    return `${month}/${day}/${year}`;
  };

  // Fetch guardians for the student
  const fetchGuardians = async () => {
    const token = localStorage.getItem("token");
    try {
      const response = await fetch(
        buildApiUrlWithParams(API_CONFIG.ENDPOINTS.STUDENT_GUARDIANS, id),
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (!response.ok) throw new Error("Failed to fetch guardians");
      const data = await response.json();
      setGuardians(data);
    } catch (error) {
      console.error("Error fetching guardians:", error);
    }
  };

  // Search guardians
  const searchGuardians = async (term) => {
    const token = localStorage.getItem("token");
    if (!term) {
      setSearchResults([]);
      return;
    }
    try {
      const response = await fetch(
        buildApiUrlWithParams(API_CONFIG.ENDPOINTS.GUARDIAN_SEARCH, term),
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (!response.ok) throw new Error("Failed to search guardians");
      const data = await response.json();
      // Filter out already linked guardians
      const linkedGuardianIds = new Set(guardians.map(g => g.guardian_id));
      const filteredResults = data.filter(g => !linkedGuardianIds.has(g.guardian_id));
      setSearchResults(filteredResults);
    } catch (error) {
      console.error("Error searching guardians:", error);
    }
  };

  // Link guardian to student
  const linkGuardian = async (guardian) => {
    const token = localStorage.getItem("token");
    try {
      const response = await fetch(
        buildApiUrl(API_CONFIG.ENDPOINTS.STUDENT_GUARDIAN_LINK),
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            student_id: id,
            guardian_id: guardian.guardian_id,
          }),
        }
      );
      if (!response.ok) throw new Error("Failed to link guardian");
      await fetchGuardians();
      setSearchTerm("");
      setSearchResults([]);
    } catch (error) {
      console.error("Error linking guardian:", error);
    }
  };

  // Unlink guardian from student
  const unlinkGuardian = async (guardian) => {
    const token = localStorage.getItem("token");
    try {
      const response = await fetch(
        buildApiUrlWithParams(API_CONFIG.ENDPOINTS.STUDENT_GUARDIANS, id) + `/${guardian.guardian_id}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (!response.ok) throw new Error("Failed to unlink guardian");
      
      // Check if this was the last student for this guardian
      const guardianResponse = await fetch(
        buildApiUrlWithParams(API_CONFIG.ENDPOINTS.GUARDIAN_DETAIL, guardian.guardian_id),
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (!guardianResponse.ok) throw new Error("Failed to fetch guardian details");
      const guardianData = await guardianResponse.json();
      
      if (guardianData.students && guardianData.students.length === 0) {
        setGuardianToDelete(guardian);
        setShowDeleteConfirm(true);
      } else {
        await fetchGuardians();
      }
    } catch (error) {
      console.error("Error unlinking guardian:", error);
    }
  };

  // Delete guardian
  const deleteGuardian = async () => {
    const token = localStorage.getItem("token");
    try {
      const response = await fetch(
        buildApiUrlWithParams(API_CONFIG.ENDPOINTS.GUARDIAN_DETAIL, guardianToDelete.guardian_id),
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (!response.ok) throw new Error("Failed to delete guardian");
      setShowDeleteConfirm(false);
      setGuardianToDelete(null);
      await fetchGuardians();
    } catch (error) {
      console.error("Error deleting guardian:", error);
    }
  };

  // Update guardian
  const updateGuardian = async (guardian) => {
    const token = localStorage.getItem("token");
    try {
      const response = await fetch(
        buildApiUrl(API_CONFIG.ENDPOINTS.GUARDIANS),
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(guardian),
        }
      );
      if (!response.ok) throw new Error("Failed to update guardian");
      setEditingGuardian(null);
      await fetchGuardians();
    } catch (error) {
      console.error("Error updating guardian:", error);
    }
  };

  // Add new guardian and link to student
  const addAndLinkNewGuardian = async () => {
    const token = localStorage.getItem("token");
    try {
      // First create the guardian
      const guardianResponse = await fetch(
        buildApiUrl(API_CONFIG.ENDPOINTS.GUARDIANS),
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(newGuardian),
        }
      );

      if (!guardianResponse.ok) throw new Error("Failed to create guardian");
      const guardianData = await guardianResponse.json();

      // Then link the guardian to the student
      const linkResponse = await fetch(
        buildApiUrl(API_CONFIG.ENDPOINTS.STUDENT_GUARDIAN_LINK),
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            student_id: id,
            guardian_id: guardianData.guardian_id,
          }),
        }
      );

      if (!linkResponse.ok) throw new Error("Failed to link guardian");
      
      // Refresh guardians list and reset form
      await fetchGuardians();
      setShowNewGuardianForm(false);
      setNewGuardian({
        first_name: "",
        last_name: "",
        relationship: "",
        email: "",
        phone: "",
        financial_notification_ok: false
      });
    } catch (error) {
      console.error("Error adding and linking guardian:", error);
      alert("Failed to add and link guardian. Please try again.");
    }
  };

  const handleNewGuardianChange = (e) => {
    const { name, value, type, checked } = e.target;
    setNewGuardian(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Promotion editing functions
  const handleEditPromotion = (promotion, index) => {
    // Parse the student_rank to extract rank and stripes
    const rankMatch = promotion.student_rank.match(/^(.+?) - (\d+)$/);
    const rank = rankMatch ? rankMatch[1] : '';
    const stripes = rankMatch ? parseInt(rankMatch[2]) : 0;
    
    setEditingPromotion({
      promotion_id: promotion.promotion_id,
      rank: rank,
      stripes: stripes.toString(),
      date_promoted: promotion.date_promoted,
      is_junior: promotion.is_junior,
      is_current: promotion.is_current
    });
    setEditingPromotionIndex(index);
  };

  const handleCancelEditPromotion = () => {
    setEditingPromotion(null);
    setEditingPromotionIndex(null);
  };

  const handleSavePromotion = async () => {
    try {
      // Basic validation
      if (!editingPromotion.rank || editingPromotion.rank.trim() === '') {
        alert('Please select a rank');
        return;
      }
      
      if (editingPromotion.stripes < 0) {
        alert('Stripes must be 0 or greater');
        return;
      }
      
      if (!editingPromotion.date_promoted) {
        alert('Please select a promotion date');
        return;
      }

      const token = localStorage.getItem("token");
      
      // Determine if this is a new promotion or editing existing
      const isNewPromotion = editingPromotionIndex === -1;
      
      const response = await fetch(
        buildApiUrlWithParams(API_CONFIG.ENDPOINTS.STUDENT_PROMOTIONS, id),
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(editingPromotion)
        }
      );

      if (response.ok) {
        // Refresh promotions data
        const promotionsResponse = await fetch(
          buildApiUrlWithParams(API_CONFIG.ENDPOINTS.STUDENT_PROMOTIONS, id),
          {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );
        
        if (promotionsResponse.ok) {
          const promotionsData = await promotionsResponse.json();
          setPromotions(promotionsData);
        }
        
        setEditingPromotion(null);
        setEditingPromotionIndex(null);
        
        // Show success message
        if (isNewPromotion) {
          alert('Promotion added successfully!');
        } else {
          alert('Promotion updated successfully!');
        }
      } else {
        const errorData = await response.json();
        alert(`Failed to ${isNewPromotion ? 'add' : 'update'} promotion: ${errorData.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error updating promotion:', error);
      alert('Error updating promotion. Please try again.');
    }
  };

  const handlePromotionChange = (field, value) => {
    setEditingPromotion(prev => ({
      ...prev,
      [field]: value
    }));
  };

  useEffect(() => {
    const fetchStudent = async () => {
      const token = localStorage.getItem("token");

      if (!token) {
        console.error("No authentication token found.");
        return;
      }

      const decoded = JSON.parse(atob(token.split(".")[1]));
      setGroups(decoded["cognito:groups"]);

      try {
        // Always fetch belt ranks for both new and existing students
        const ranksResponse = await fetch(
          buildApiUrl(API_CONFIG.ENDPOINTS.RANKS),
          {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );

        if (ranksResponse.ok) {
          const ranksData = await ranksResponse.json();
          setBeltRanks(ranksData);
        }

        // Only fetch student data if we're editing an existing student
        if (!isNewStudent) {
          // Fetch student data
          const studentResponse = await fetch(
            buildApiUrlWithParams(API_CONFIG.ENDPOINTS.STUDENT_DETAIL, id),
            {
              method: "GET",
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
            }
          );

          if (!studentResponse.ok) {
            throw new Error("Failed to fetch student data");
          }

          const studentData = await studentResponse.json();
          console.log("Fetched student:", studentData);

          // ✅ Convert date fields before setting state
          setStudent({
            ...studentData,
            date_of_birth: formatDateForInput(studentData.date_of_birth),
            date_last_promoted: formatDateForInput(studentData.date_last_promoted),
          });

          // Fetch promotions data
          const promotionsResponse = await fetch(
            buildApiUrlWithParams(API_CONFIG.ENDPOINTS.STUDENT_PROMOTIONS, id),
            {
              method: "GET",
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
            }
          );

          if (!promotionsResponse.ok) {
            throw new Error("Failed to fetch promotions data");
          }

          const promotionsData = await promotionsResponse.json();
          console.log("Fetched promotions:", promotionsData);
          setPromotions(promotionsData);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
      setLoading(false);
    };

    fetchStudent();
    if (id) {
      fetchGuardians();
    }
  }, [id, isNewStudent]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let formattedValue = value;

    // Handle checkbox/switch inputs that pass checked instead of value
    if (name === 'active') {
      formattedValue = value;
    }

    // Format phone number as user types
    if (name === 'phone') {
      formattedValue = formatPhoneNumber(value);
    }

    // Format tuition amount to 2 decimal places
    if (name === 'tuition_amount' && value) {
      const num = parseFloat(value);
      if (!isNaN(num)) {
        formattedValue = num.toFixed(2);
      }
      else {
        formattedValue = "120.00";
      }
    }

    // Format state to uppercase and limit to 2 characters
    if (name === 'state') {
      formattedValue = value.toUpperCase().substring(0, 2);
    }

    // Format zip to only allow numbers and limit to 5 digits
    if (name === 'zip') {
      formattedValue = value.replace(/\D/g, '').substring(0, 5);
    }

    // Update student state
    setStudent(prev => ({
      ...prev,
      [name]: formattedValue
    }));

    // Validate fields
    const newErrors = { ...errors };
    switch (name) {
      case 'email':
        if (value && !validateEmail(value)) {
          newErrors.email = 'Please enter a valid email address';
        } else {
          delete newErrors.email;
        }
        break;
      case 'phone':
        if (value && !validatePhone(value)) {
          newErrors.phone = 'Please enter a valid phone number (###-###-####)';
        } else {
          delete newErrors.phone;
        }
        break;
      case 'date_of_birth':
      case 'date_last_promoted':
        if (value) {
          const formattedDate = formatDateForAPI(value);
          if (!validateDate(formattedDate)) {
            newErrors[name] = 'Please enter a valid date (MM-DD-YYYY)';
          } else {
            delete newErrors[name];
          }
        }
        break;
      case 'tuition_amount':
        if (value && !validateTuition(value)) {
          newErrors.tuition_amount = 'Please enter a valid amount with 2 decimal places (e.g., 100.00)';
        } else {
          delete newErrors.tuition_amount;
        }
        break;
      case 'state':
        if (value && !validateState(value)) {
          newErrors.state = 'Please enter a valid 2-letter state code';
        } else {
          delete newErrors.state;
        }
        break;
      case 'zip':
        if (value && !validateZip(value)) {
          newErrors.zip = 'Please enter a valid 5-digit zip code';
        } else {
          delete newErrors.zip;
        }
        break;
    }
    setErrors(newErrors);
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (student.email && !validateEmail(student.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (student.phone && !validatePhone(student.phone)) {
      newErrors.phone = 'Please enter a valid phone number (###-###-####)';
    }
    
    if (student.date_of_birth) {
      const formattedDate = formatDateForAPI(student.date_of_birth);
      if (!validateDate(formattedDate)) {
        newErrors.date_of_birth = 'Please enter a valid date (MM-DD-YYYY)';
      }
    }
    
    if (student.date_last_promoted) {
      const formattedDate = formatDateForAPI(student.date_last_promoted);
      if (!validateDate(formattedDate)) {
        newErrors.date_last_promoted = 'Please enter a valid date (MM-DD-YYYY)';
      }
    }

    if (student.tuition_amount && !validateTuition(student.tuition_amount)) {
      newErrors.tuition_amount = 'Please enter a valid amount with 2 decimal places (e.g., 100.00)';
    }

    if (student.state && !validateState(student.state)) {
      newErrors.state = 'Please enter a valid 2-letter state code';
    }

    if (student.zip && !validateZip(student.zip)) {
      newErrors.zip = 'Please enter a valid 5-digit zip code';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      alert('Please correct the errors before submitting');
      return;
    }

    const token = localStorage.getItem("token");

    const formattedStudent = {
      ...student,
      date_of_birth: formatDateForAPI(student.date_of_birth),
      date_last_promoted: formatDateForAPI(student.date_last_promoted),
      active: student.active
    };

    try {
      const response = await fetch(
        buildApiUrl(API_CONFIG.ENDPOINTS.STUDENTS),
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(formattedStudent),
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to ${isNewStudent ? 'create' : 'update'} student`);
      }

      alert(`Student ${isNewStudent ? 'created' : 'updated'} successfully!`);
      navigate("/students");
    } catch (error) {
      console.error(`Error ${isNewStudent ? 'creating' : 'updating'} student:`, error);
      alert(`Failed to ${isNewStudent ? 'create' : 'update'} student`);
    }
  };

  const handleImageUpload = async (event, type = 'student') => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.type.includes('image/jpeg')) {
      alert('Please select a JPG image file.');
      return;
    }

    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64Data = reader.result.split(',')[1];
        
        const token = localStorage.getItem("token");
        const response = await fetch(
          buildApiUrlWithParams(API_CONFIG.ENDPOINTS.STUDENT_IMAGE, id, type),
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              image: base64Data,
              extension: 'jpg'
            }),
          }
        );

        if (!response.ok) {
          throw new Error("Failed to upload image");
        }

        const result = await response.json();
        setStudent(prev => ({
          ...prev,
          [type === 'student' ? 'student_pic_guid' : 'release_pic_guid']: result.student_pic_guid
        }));
        setUploading(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error("Error uploading image:", error);
      alert("Failed to upload image. Please try again.");
      setUploading(false);
    }
  };

  // Add the Guardians tab content
  const renderGuardiansTab = () => (
    <Tab eventKey="guardians" title="Guardians">
      <Card>
        <Card.Body>
          <h4>Linked Guardians</h4>
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>First Name</th>
                <th>Last Name</th>
                <th>Relationship</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Finance Notification</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {guardians.map((guardian) => (
                <tr key={guardian.guardian_id}>
                  <td>
                    {editingGuardian?.guardian_id === guardian.guardian_id ? (
                      <Form.Control
                        type="text"
                        value={editingGuardian.first_name}
                        onChange={(e) => setEditingGuardian({ ...editingGuardian, first_name: e.target.value })}
                      />
                    ) : (
                      guardian.first_name
                    )}
                  </td>
                  <td>
                    {editingGuardian?.guardian_id === guardian.guardian_id ? (
                      <Form.Control
                        type="text"
                        value={editingGuardian.last_name}
                        onChange={(e) => setEditingGuardian({ ...editingGuardian, last_name: e.target.value })}
                      />
                    ) : (
                      guardian.last_name
                    )}
                  </td>
                  <td>
                    {editingGuardian?.guardian_id === guardian.guardian_id ? (
                      <Form.Control
                        type="text"
                        value={editingGuardian.relationship}
                        onChange={(e) => setEditingGuardian({ ...editingGuardian, relationship: e.target.value })}
                      />
                    ) : (
                      guardian.relationship
                    )}
                  </td>
                  <td>
                    {editingGuardian?.guardian_id === guardian.guardian_id ? (
                      <Form.Control
                        type="email"
                        value={editingGuardian.email}
                        onChange={(e) => setEditingGuardian({ ...editingGuardian, email: e.target.value })}
                      />
                    ) : (
                      guardian.email
                    )}
                  </td>
                  <td>
                    {editingGuardian?.guardian_id === guardian.guardian_id ? (
                      <Form.Control
                        type="tel"
                        value={editingGuardian.phone}
                        onChange={(e) => setEditingGuardian({ ...editingGuardian, phone: e.target.value })}
                      />
                    ) : (
                      guardian.phone
                    )}
                  </td>
                  <td>
                    {editingGuardian?.guardian_id === guardian.guardian_id ? (
                      <Form.Check
                      type="switch"
                      id="finance-notification-switch"
                      label="Finance Notification"
                      name="finance_notification"
                      checked={editingGuardian.financial_notification_ok}
                      onChange={(e) => setEditingGuardian({ ...editingGuardian, financial_notification_ok: e.target.checked })}
                      />
                    ) : (
                      guardian.financial_notification_ok ? 'Yes' : 'No'
                    )}
                  </td>
                  <td>
                    {editingGuardian?.guardian_id === guardian.guardian_id ? (
                      <>
                        <Button
                          variant="success"
                          size="sm"
                          onClick={() => updateGuardian(editingGuardian)}
                          className="me-2"
                        >
                          Save
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => setEditingGuardian(null)}
                        >
                          Cancel
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => setEditingGuardian(guardian)}
                          className="me-2"
                        >
                          Edit
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => unlinkGuardian(guardian)}
                        >
                          Unlink
                        </Button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          <h4 className="mt-4">Link New Guardian</h4>
          <div className="mb-3">
            <Button 
              variant="outline-primary" 
              onClick={() => setShowNewGuardianForm(!showNewGuardianForm)}
            >
              {showNewGuardianForm ? 'Cancel' : 'Add New Guardian'}
            </Button>
          </div>

          {showNewGuardianForm && (
            <Card className="mb-3">
              <Card.Body>
                <h5>New Guardian Information</h5>
                <Form>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>First Name</Form.Label>
                        <Form.Control
                          type="text"
                          name="first_name"
                          value={newGuardian.first_name}
                          onChange={handleNewGuardianChange}
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Last Name</Form.Label>
                        <Form.Control
                          type="text"
                          name="last_name"
                          value={newGuardian.last_name}
                          onChange={handleNewGuardianChange}
                          required
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Relationship</Form.Label>
                        <Form.Control
                          type="text"
                          name="relationship"
                          value={newGuardian.relationship}
                          onChange={handleNewGuardianChange}
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Phone</Form.Label>
                        <Form.Control
                          type="text"
                          name="phone"
                          value={newGuardian.phone}
                          onChange={handleNewGuardianChange}
                          placeholder="###-###-####"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={12}>
                      <Form.Group className="mb-3">
                        <Form.Label>Email</Form.Label>
                        <Form.Control
                          type="email"
                          name="email"
                          value={newGuardian.email}
                          onChange={handleNewGuardianChange}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Form.Group className="mb-3">
                    <Form.Check
                      type="switch"
                      id="finance-notification-switch"
                      label="Finance Notification"
                      name="financial_notification_ok"
                      checked={newGuardian.financial_notification_ok}
                      onChange={handleNewGuardianChange}
                    />
                  </Form.Group>
                  <Button 
                    variant="primary" 
                    onClick={addAndLinkNewGuardian}
                    disabled={!newGuardian.first_name || !newGuardian.last_name || !newGuardian.relationship}
                  >
                    Add Guardian
                  </Button>
                </Form>
              </Card.Body>
            </Card>
          )}

          <Form.Group className="mb-3">
            <Form.Label>Search Guardians</Form.Label>
            <Form.Control
              type="text"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                searchGuardians(e.target.value);
              }}
              placeholder="Search by name..."
            />
          </Form.Group>

          {searchResults.length > 0 && (
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {searchResults.map((guardian) => (
                  <tr key={guardian.guardian_id}>
                    <td>{guardian.first_name} {guardian.last_name}</td>
                    <td>{guardian.email}</td>
                    <td>{guardian.phone}</td>
                    <td>
                      <Button
                        variant="success"
                        size="sm"
                        onClick={() => linkGuardian(guardian)}
                      >
                        Link
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <Modal show={showDeleteConfirm} onHide={() => setShowDeleteConfirm(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Delete Guardian</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          This guardian is not linked to any students. Would you like to delete them from the system?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteConfirm(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={deleteGuardian}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </Tab>
  );

  if (loading) return <p className="text-center text-light">Loading...</p>;

  return (
    <div>
      <Navbar groups={groups}/>
      <Container className="mt-4">
        <Card className="mb-4">
          <Card.Body>
            <Row>
              {/* Header Section - Always Visible */}
              <Col md={3} className="text-center">
                <div className="position-relative">
                  <Image
                    src={student.student_pic_guid ? buildS3Url(student.student_pic_guid) : "https://via.placeholder.com/150"}
                    rounded
                    className="mb-3"
                    style={{ width: '150px', height: '180px', objectFit: 'cover', objectPosition: 'top center' }}
                  />
                  <label className="position-absolute bottom-0 end-0 bg-primary rounded-circle p-2" style={{ cursor: 'pointer' }}>
                    <Camera color="white" size={20} />
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageUpload(e, 'student')}
                      style={{ display: 'none' }}
                    />
                  </label>
                </div>
              </Col>
              <Col md={9}>
                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>First Name</Form.Label>
                      <Form.Control
                        type="text"
                        name="first_name"
                        value={student.first_name}
                        onChange={handleChange}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Last Name</Form.Label>
                      <Form.Control
                        type="text"
                        name="last_name"
                        value={student.last_name}
                        onChange={handleChange}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Nickname</Form.Label>
                      <Form.Control
                        type="text"
                        name="nick_name"
                        value={student.nick_name}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md={3}>
                    <Form.Group className="mb-3">
                      <Form.Label>Current Rank</Form.Label>
                      {isNewStudent ? (
                        <Form.Select
                          name="current_rank"
                          value={student.current_rank}
                          onChange={handleChange}
                        >
                          <option value="">Select Rank</option>
                          {beltRanks && beltRanks.length > 0 ? beltRanks.map((rank) => (
                            <option key={rank.rank_id} value={rank.rank}>
                              {rank.rank}
                            </option>
                          )) : (
                            <option value="" disabled>Loading ranks...</option>
                          )}
                        </Form.Select>
                      ) : (
                        <Form.Control
                          type="text"
                          name="current_rank"
                          value={student.current_rank}
                          onChange={handleChange}
                          readOnly
                          className="bg-light"
                        />
                      )}
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group className="mb-3">
                      <Form.Label>Current Stripes</Form.Label>
                      <Form.Control
                        type="text"
                        name="current_stripes"
                        value={student.current_stripes}
                        onChange={handleChange}
                        readOnly={!isNewStudent}
                        className={!isNewStudent ? 'bg-light' : ''}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group className="mb-3">
                      <Form.Label>Date Promoted</Form.Label>
                      <Form.Control
                        type="date"
                        name="date_last_promoted"
                        value={student.date_last_promoted}
                        onChange={handleChange}
                        readOnly={!isNewStudent}
                        className={!isNewStudent ? 'bg-light' : ''}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group className="mb-3">
                      <Form.Check
                        type="switch"
                        id="active-switch"
                        label="Active"
                        name="active"
                        checked={student.active}
                        onChange={(e) => handleChange({ target: { name: 'active', value: e.target.checked } })}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Tabbed Sections */}
        <Tabs defaultActiveKey="personal" className="mb-3">
          {/* Personal Information Tab */}
          <Tab eventKey="personal" title="Personal">
            <Card>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Date of Birth</Form.Label>
                      <Form.Control
                        type="date"
                        name="date_of_birth"
                        value={student.date_of_birth}
                        onChange={handleChange}
                      />
                      {errors.date_of_birth && <div className="text-danger">{errors.date_of_birth}</div>}
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Phone</Form.Label>
                      <Form.Control
                        type="text"
                        name="phone"
                        value={student.phone}
                        onChange={handleChange}
                        placeholder="###-###-####"
                      />
                      {errors.phone && <div className="text-danger">{errors.phone}</div>}
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Email</Form.Label>
                      <Form.Control
                        type="email"
                        name="email"
                        value={student.email}
                        onChange={handleChange}
                      />
                      {errors.email && <div className="text-danger">{errors.email}</div>}
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>AAU Number</Form.Label>
                      <Form.Control
                        type="text"
                        name="aau_number"
                        value={student.aau_number}
                        onChange={handleChange}
                        placeholder="Enter AAU number"
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Tab>

          {/* Promotions Tab */}
          <Tab eventKey="promotions" title="Promotions">
            <Card>
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h4>Promotions History</h4>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => {
                      setEditingPromotion({
                        rank: '',
                        stripes: 0,
                        date_promoted: new Date().toISOString().split('T')[0],
                        is_junior: false,
                        is_current: false
                      });
                      setEditingPromotionIndex(-1); // -1 indicates new promotion
                    }}
                  >
                    Add Promotion
                  </Button>
                </div>
                {loading ? (
                  <div className="text-center">
                    <Spinner animation="border" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </Spinner>
                  </div>
                ) : (
                  <>
                    {promotions.length === 0 && editingPromotionIndex !== -1 ? (
                      <p>No promotion history available.</p>
                    ) : (
                      <Table striped bordered hover responsive>
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th>Rank</th>
                            <th>Junior</th>
                            <th>Current</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {/* New Promotion Row */}
                          {editingPromotionIndex === -1 && editingPromotion && (
                            <tr className="table-warning">
                              <td>
                                <Form.Control
                                  type="date"
                                  value={editingPromotion.date_promoted}
                                  onChange={(e) => handlePromotionChange('date_promoted', e.target.value)}
                                />
                              </td>
                              <td>
                                <Row>
                                  <Col md={6}>
                                    <Form.Select
                                      value={editingPromotion.rank}
                                      onChange={(e) => handlePromotionChange('rank', e.target.value)}
                                    >
                                      <option value="">Select Rank</option>
                                      {beltRanks && beltRanks.length > 0 ? beltRanks.map((rank) => (
                                        <option key={rank.rank_id} value={rank.rank}>
                                          {rank.rank}
                                        </option>
                                      )) : (
                                        <option value="" disabled>Loading ranks...</option>
                                      )}
                                    </Form.Select>
                                  </Col>
                                  <Col md={6}>
                                    <Form.Control
                                      type="number"
                                      min="0"
                                      value={editingPromotion.stripes}
                                      onChange={(e) => handlePromotionChange('stripes', parseInt(e.target.value))}
                                      placeholder="Stripes"
                                    />
                                  </Col>
                                </Row>
                              </td>
                              <td>
                                <Form.Check
                                  type="checkbox"
                                  checked={editingPromotion.is_junior}
                                  onChange={(e) => handlePromotionChange('is_junior', e.target.checked)}
                                />
                              </td>
                              <td>
                                <Form.Check
                                  type="checkbox"
                                  checked={editingPromotion.is_current}
                                  onChange={(e) => handlePromotionChange('is_current', e.target.checked)}
                                />
                              </td>
                              <td>
                                <div>
                                  <Button
                                    size="sm"
                                    variant="success"
                                    className="me-2"
                                    onClick={handleSavePromotion}
                                  >
                                    Save
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="secondary"
                                    onClick={handleCancelEditPromotion}
                                  >
                                    Cancel
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          )}
                          {promotions.map((promotion, index) => (
                            <tr key={index}>
                              <td>
                                {editingPromotionIndex === index && editingPromotion ? (
                                  <Form.Control
                                    type="date"
                                    value={editingPromotion.date_promoted}
                                    onChange={(e) => handlePromotionChange('date_promoted', e.target.value)}
                                  />
                                ) : (
                                  formatDateForDisplay(promotion.date_promoted)
                                )}
                              </td>
                              <td>
                                {editingPromotionIndex === index && editingPromotion ? (
                                  <Row>
                                    <Col md={6}>
                                      <Form.Select
                                        value={editingPromotion.rank}
                                        onChange={(e) => handlePromotionChange('rank', e.target.value)}
                                      >
                                        <option value="">Select Rank</option>
                                        {beltRanks && beltRanks.length > 0 ? beltRanks.map((rank) => (
                                          <option key={rank.rank_id} value={rank.rank}>
                                            {rank.rank}
                                          </option>
                                        )) : (
                                          <option value="" disabled>Loading ranks...</option>
                                        )}
                                      </Form.Select>
                                    </Col>
                                    <Col md={6}>
                                      <Form.Control
                                        type="number"
                                        min="0"
                                        value={editingPromotion.stripes}
                                        onChange={(e) => handlePromotionChange('stripes', parseInt(e.target.value))}
                                        placeholder="Stripes"
                                      />
                                    </Col>
                                  </Row>
                                ) : (
                                  promotion.student_rank
                                )}
                              </td>
                              <td>
                                {editingPromotionIndex === index && editingPromotion ? (
                                  <Form.Check
                                    type="checkbox"
                                    checked={editingPromotion.is_junior}
                                    onChange={(e) => handlePromotionChange('is_junior', e.target.checked)}
                                  />
                                ) : (
                                  promotion.is_junior ? 'Yes' : 'No'
                                )}
                              </td>
                              <td>
                                {editingPromotionIndex === index && editingPromotion ? (
                                  <Form.Check
                                    type="checkbox"
                                    checked={editingPromotion.is_current}
                                    onChange={(e) => handlePromotionChange('is_current', e.target.checked)}
                                  />
                                ) : (
                                  promotion.is_current ? 'Yes' : 'No'
                                )}
                              </td>
                              <td>
                                {editingPromotionIndex === index && editingPromotion ? (
                                  <div>
                                    <Button
                                      size="sm"
                                      variant="success"
                                      className="me-2"
                                      onClick={handleSavePromotion}
                                    >
                                      Save
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="secondary"
                                      onClick={handleCancelEditPromotion}
                                    >
                                      Cancel
                                    </Button>
                                  </div>
                                ) : (
                                  <Button
                                    size="sm"
                                    variant="outline-primary"
                                    onClick={() => handleEditPromotion(promotion, index)}
                                  >
                                    Edit
                                  </Button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    )}
                  </>
                )}
              </Card.Body>
            </Card>
          </Tab>

          {/* Guardians Tab */}
          {renderGuardiansTab()}

          {/* Address Information Tab */}
          <Tab eventKey="address" title="Address">
            <Card>
              <Card.Body>
                <Row>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>Street Address 1</Form.Label>
                      <Form.Control
                        type="text"
                        name="street_1"
                        value={student.street_1}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>Street Address 2</Form.Label>
                      <Form.Control
                        type="text"
                        name="street_2"
                        value={student.street_2}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>City</Form.Label>
                      <Form.Control
                        type="text"
                        name="city"
                        value={student.city}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>State</Form.Label>
                      <Form.Control
                        type="text"
                        name="state"
                        value={student.state}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>ZIP Code</Form.Label>
                      <Form.Control
                        type="text"
                        name="zip"
                        value={student.zip}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Tab>

          {/* Medical Information Tab - Only for mtkd-admin */}
          {groups.includes("mtkd-admin") && (
            <Tab eventKey="medical" title="Medical">
              <Card>
                <Card.Body>
                  <Row>
                    <Col md={12}>
                      <Form.Group className="mb-3">
                        <Form.Label>Medical Conditions</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          name="medical_conditions"
                          value={student.medical_conditions}
                          onChange={handleChange}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={12}>
                      <Form.Group className="mb-3">
                        <Form.Label>Allergies</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          name="allergies"
                          value={student.allergies}
                          onChange={handleChange}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Tab>
          )}

          {/* Billing Information Tab - Only for mtkd-finance */}
          {groups.includes("mtkd-finance") && (
            <Tab eventKey="billing" title="Billing">
              <Card>
                <Card.Body>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Tuition Amount</Form.Label>
                        <Form.Control
                          type="text"
                          name="tuition_amount"
                          value={student.tuition_amount}
                          onChange={handleChange}
                        />
                        {errors.tuition_amount && <div className="text-danger">{errors.tuition_amount}</div>}
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Tuition Due Day</Form.Label>
                        <Form.Control
                          type="number"
                          name="tuition_due_day"
                          value={student.tuition_due_day}
                          onChange={handleChange}
                          min="1"
                          max="31"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Tab>
          )}

          {/* Documents Tab */}
          <Tab eventKey="documents" title="Documents">
            <Card>
              <Card.Body>
                <Row>
                  <Col md={12}>
                    <h5 className="mb-3">Release Form</h5>
                    <div style={{ 
                      width: '100%', 
                      height: '400px',
                      margin: '0 auto',
                      border: '1px solid #dee2e6',
                      borderRadius: '8px',
                      overflow: 'auto'
                    }}>
                      {student.release_pic_guid ? (
                        <img
                          src={buildS3Url(student.release_pic_guid)}
                          alt={`Release form for ${student.first_name} ${student.last_name}`}
                          style={{ 
                            width: '100%',
                            height: 'auto',
                            objectFit: 'contain',
                            objectPosition: 'bottom center'
                          }}
                        />
                      ) : (
                        <div className="d-flex align-items-center justify-content-center h-100 bg-light">
                          <Camera size={100} className="text-secondary" />
                        </div>
                      )}
                    </div>
                    <div className="mt-2">
                      <input
                        type="file"
                        accept=".jpg,.jpeg"
                        onChange={(e) => handleImageUpload(e, 'release')}
                        style={{ display: 'none' }}
                        id="releaseImageUpload"
                        disabled={uploading}
                      />
                      <label 
                        htmlFor="releaseImageUpload" 
                        className={`btn btn-primary ${uploading ? 'disabled' : ''}`}
                      >
                        {uploading ? 'Uploading...' : 'Upload Release Form'}
                      </label>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Tab>
        </Tabs>

        {/* Action Buttons */}
        <div className="d-flex justify-content-between mb-4">
          <Button variant="secondary" onClick={() => navigate('/students')}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit}>
            {isNewStudent ? 'Create Student' : 'Update Student'}
          </Button>
        </div>
      </Container>
    </div>
  );
};

export default StudentDetail;
